<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2" v-if="isFetched && mapPanelDataFetched">
      <card-title-row translation-path="sidebar.graphGeneration" :has-buttons="false">
        <template #subtitle>
          <italic-subheader translation-path="sidebar.saveGraphToFile" class="text-left" />
        </template>
      </card-title-row>
      <profiles-widget
        v-if="isEpanetProfilesEnabled"
        class="mt-3"
        border-bottom
        v-bind="profilesData"
        :current-data="getProfilePayload()"
        :is-add-profile-disabled="isStickyButtonsValid"
        use-user-permissions
        @changeCurrentProfile="changeCurrentEpanetProfile"
      ></profiles-widget>
      <card-title-row is-subtitle translation-path="sidebar.defineExportParameters" class="pt-5" />
      <sidebar-row class="pt-2">
        <sidebar-select-row
          font-size="14px"
          :value="statusModel"
          :select-props="statusProps"
          @input="statusModel = $event"
          translation-path="sidebar.status"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20, 'nudge-top': -5 }"
          required
          align="center"
        />
      </sidebar-row>
      <sidebar-row>
        <sidebar-select-row
          class="my-5"
          font-size="14px"
          :value="netKindModel"
          :select-props="netKindProps"
          @input="netKindModel = $event"
          translation-path="sidebar.networkType"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20 }"
          required
          align="center"
        />
      </sidebar-row>
      <sidebar-row>
        <graph-generation-params-list :parameters.sync="additionalGraphParameters" />
      </sidebar-row>
      <sidebar-row class="pt-4" border-top>
        <v-checkbox v-model="useBillingData" class="pt-6">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.useBillingData') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row>
        <sidebar-form
          v-if="isEpanetDailyUsageEnabled"
          :is-valid.sync="isAttrsFormValid"
          ref="watermetersForm"
          :class="{ 'attrs_form--disabled': !useBillingData }"
          v-model="watermetersParams"
          :inputs="watermetersInputs"
          :has-icon-column="false"
          is-editing-on
        >
          <template v-for="input of watermetersInputs" #[input.name]="data">
            <v-col :key="input.name" class="pa-0">
              <data-input v-model="watermetersParams[input.name]" v-bind="data" />
            </v-col>
          </template>
        </sidebar-form>
        <sidebar-date-picker-row
          v-else
          translation-path="sidebar.computeWaterUsage"
          :value="waterUsageDate"
          @input="waterUsageDate = $event"
          :disabled="!useBillingData"
        />
      </sidebar-row>
      <sidebar-row class="pb-3">
        <v-row no-gutters class="justify-space-between">
          <v-col cols="4" class="billingDataText" :class="useBillingData ? '' : 'text--disabled'">
            {{ $i18n.t('sidebar.selectBillingDemandMethod') }}:
          </v-col>
          <v-col cols="7">
            <v-radio-group v-model="billing_demand_method" :disabled="!useBillingData">
              <v-radio value="closest">
                <template #label>
                  <div :class="useBillingData ? 'billingDataText' : 'billingDataTextDisabled'">
                    {{ $i18n.t('sidebar.billingDemandMethodClosest') }}
                  </div>
                </template>
              </v-radio>
              <v-radio value="divide">
                <template #label>
                  <div :class="useBillingData ? 'billingDataText' : 'billingDataTextDisabled'">
                    {{ $i18n.t('sidebar.billingDemandMethodDivide') }}
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-checkbox :disabled="!useBillingData" hide-details v-model="billing_use_base_demand">
              <template #label>
                <div :class="useBillingData ? 'billingDataText' : 'billingDataTextDisabled'">
                  {{ $i18n.t('sidebar.useBillingDemand') }}
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row v-if="isScadaJunctionsEnabled" border-top class="pt-4">
        <v-checkbox v-model="useScadaData" class="pt-6">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.useScadaData') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row v-if="isScadaJunctionsEnabled">
        <v-row no-gutters class="justify-space-between">
          <v-col cols="4" class="billingDataText" :class="useScadaData ? '' : 'text--disabled'">
            {{ $i18n.t('sidebar.scadaSince') }}:
          </v-col>
          <v-col cols="7">
            <data-input
              :data-type="dataTypeDate"
              v-model="scada_since"
              :disabled="!useScadaData"
              prepend-icon="mdi-calendar"
            />
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row v-if="isNetDevelopmentPlaningEnabled" border-top class="pt-4">
        <v-checkbox v-model="useNetDevelopmentPlaning" class="pt-6">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.useDevelopmentPlaning') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row v-if="isNetDevelopmentPlaningEnabled">
        <v-row no-gutters class="justify-space-between">
          <v-col cols="4" class="billingDataText" :class="useNetDevelopmentPlaning ? '' : 'text--disabled'">
            {{ $i18n.t('sidebar.investmentType') }}:
          </v-col>
          <v-col cols="7">
            <data-input
              :items="netDevelopmentDictionary.values || []"
              :data-type="{ name: 'select' }"
              return-object
              v-model="netDevelopmentVariant"
              :disabled="!useNetDevelopmentPlaning"
              :rules="[v => !!v || v == '0' || 'rules.required']"
            />
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row v-if="isUseDmaJunctionsDemandEnabled" class="pt-4" border-top>
        <v-checkbox v-model="use_dma_junctions_demand" class="pt-6">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.dmaJunctionsDemand') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row v-if="isUseDmaJunctionsDemandEnabled">
        <v-form
          v-model="isDmaJunctionsDemandFormValid"
          ref="dmaJunctionsDemandForm"
          :class="{ 'attrs_form--disabled': !use_dma_junctions_demand }"
        >
          <sidebar-form
            v-model="dmaJunctionsDemandParams"
            :inputs="dmaJunctionsDemandInputs"
            :has-icon-column="false"
            is-editing-on
          >
            <template v-for="input of dmaJunctionsDemandInputs" #[input.name]="data">
              <v-col :key="input.name" class="pa-0">
                <data-input v-model="dmaJunctionsDemandParams[input.name]" v-on="input.events" v-bind="data" />
              </v-col>
            </template>
          </sidebar-form>
        </v-form>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';
import MainLoader from '@/components/MainLoader';
import SidebarSelectRow from '@/components/SidebarSelectRow';
import SidebarDatePickerRow from '@/components/SidebarDatePickerRow';
import DataInput from '@/components/DataInput';
import GraphGenerationParamsList from '@/components/GraphGenerationParamsList';
import ProfilesWidget from '@/components/ProfilesWidget';
import SidebarForm from '@/components/SidebarForm';

export default {
  name: 'GraphGeneration',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    DataInput,
    SidebarDatePickerRow,
    SidebarSelectRow,
    MainLoader,
    SidebarRow,
    ItalicSubheader,
    CardTitleRow,
    GraphGenerationParamsList,
    ProfilesWidget,
    SidebarForm,
  },
  data: vm => ({
    netDevelopmentDictionary: null,
    scada_since: localStorage.getItem('scada_since')
      ? JSON.parse(localStorage.getItem('scada_since'))
      : vm
          .$dayjs(new Date())
          .subtract(1, 'day')
          .toISOString()
          .slice(0, 10),
    statusModel: localStorage.getItem('statusModel') ? JSON.parse(localStorage.getItem('statusModel')) : [],
    netKindModel: localStorage.getItem('netKindModel') ? JSON.parse(localStorage.getItem('netKindModel')) : [],
    useBillingData: localStorage.getItem('useBillingData') ? JSON.parse(localStorage.getItem('useBillingData')) : false,
    useScadaData: localStorage.getItem('useScadaData') ? JSON.parse(localStorage.getItem('useScadaData')) : false,
    billing_demand_method: localStorage.getItem('billing_demand_method')
      ? JSON.parse(localStorage.getItem('billing_demand_method'))
      : 'closest',
    additionalGraphParameters: localStorage.getItem('additionalGraphParameters')
      ? JSON.parse(localStorage.getItem('additionalGraphParameters'))
      : [],
    dataTypeDate: { name: 'date' },
    isButtonLoading: false,
    isExportLoading: false,
    isModelLoading: false,
    isFetched: false,
    isDmaJunctionsDemandFormValid: false,
    use_dma_junctions_demand: localStorage.getItem('use_dma_junctions_demand')
      ? JSON.parse(localStorage.getItem('use_dma_junctions_demand'))
      : false,
    dmaJunctionsDemandParams: {
      dma_layer: localStorage.getItem('dma_layer') ? JSON.parse(localStorage.getItem('dma_layer')) : null,
      dma_demand: localStorage.getItem('dma_demand') ? JSON.parse(localStorage.getItem('dma_demand')) : null,
      dma_pipes: localStorage.getItem('dma_pipes') ? JSON.parse(localStorage.getItem('dma_pipes')) : null,
    },
    useNetDevelopmentPlaning: localStorage.getItem('useNetDevelopmentPlaning')
      ? JSON.parse(localStorage.getItem('useNetDevelopmentPlaning'))
      : false,
    netDevelopmentVariant: localStorage.getItem('netDevelopmentVariant')
      ? JSON.parse(localStorage.getItem('netDevelopmentVariant'))
      : null,
    waterUsageDate: [],
    isAttrsFormValid: false,
    billing_use_base_demand: localStorage.getItem('billing_use_base_demand')
      ? JSON.parse(localStorage.getItem('billing_use_base_demand'))
      : false,
    watermetersParams: {
      watermeters_layer: null,
      watermeters_daily_usage: null,
      watermeters_recipient_kind: null,
    },
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    netTypes: get('epanet/netKind'),
    statusItems: get('epanet/status'),
    developmentPlanningDictId: get('admin/modulesMapping@water.development_planning_dict_id'),
    isNetDevelopmentPlaningEnabled: get('admin/settingsValues@epanet_development_planning_enabled'),
    isEpanetRunAnalysisEnabled: get('admin/settingsValues@epanet_run_analysis_enabled'),
    isEpanetDailyUsageEnabled: get('admin/settingsValues@epanet_daily_usage_from_watermeters_attribute'),
    isScadaJunctionsEnabled: get('admin/settingsValues@scada_junctions_module_enabled'),
    isEpanetProfilesEnabled: get('admin/settingsValues@epanet_profiles_enabled'),
    isUseDmaJunctionsDemandEnabled: get('admin/settingsValues@epanet_dma_junctions_demand_enabled'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    stickyButtons: sync('sidebar/stickyButtons'),
    epanetProfiles: get('epanet/epanetProfiles'),
    dataSources: get('layers/metadata'),
    projectElements: get('layers/project@layers'),
    layers: get('layers/layers'),
    developmentPlanningAttribute: get('admin/modulesMapping@water.development_planning_attribute'),
    computedNetTypesItems() {
      const old = this.netTypes.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    computedStatusItems() {
      const old = this.statusItems.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    statusProps() {
      return {
        items: this.computedStatusItems,
        multiple: true,
        chips: true,
        autoConvertSelect: false,
        hideDetails: true,
      };
    },
    netKindProps() {
      return {
        items: this.computedNetTypesItems,
        multiple: true,
        chips: true,
        hideDetails: true,
      };
    },
    computedWaterUsage() {
      return this.waterUsageDate.map(value => {
        if (this.waterUsageDate.indexOf(value) === 1) {
          const lastDay = this.$dayjs(this.waterUsageDate[1]).daysInMonth();
          return value + `-${lastDay}`;
        } else {
          return value + '-01';
        }
      });
    },
    isValid() {
      return (
        (this.useBillingData && !this.isEpanetDailyUsageEnabled ? this.computedWaterUsage.length === 2 : true) &&
        (this.useBillingData && this.isEpanetDailyUsageEnabled ? this.isAttrsFormValid : true) &&
        (this.useScadaData ? this.scada_since : true) &&
        (this.isUseDmaJunctionsDemandEnabled && this.use_dma_junctions_demand
          ? this.isDmaJunctionsDemandFormValid
          : true) &&
        (this.useNetDevelopmentPlaning ? this.netDevelopmentVariant : true) &&
        this.netKindModel.length > 0 &&
        this.statusModel.length > 0
      );
    },
    computedStickybuttons() {
      return [
        {
          action: 'generateGraph',
          translationPath: 'sidebar.export',
          loading: this.isExportLoading,
          disabled: this.isModelLoading,
        },
        ...(this.isEpanetRunAnalysisEnabled
          ? [
              {
                action: 'epanetAnalysisModel',
                translationPath: 'sidebar.model',
                cancelLike: true,
                loading: this.isModelLoading,
                disabled: this.isExportLoading,
              },
            ]
          : []),
      ];
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: 'dialog.title.profiles.epanetAdd',
          editProfileDialogTitle: 'dialog.title.profiles.epanetEdit',
          deleteProfileDialogTitle: 'dialog.title.profiles.epanetDelete',
        },
        profiles: this.epanetProfiles,
        getProfiles: this.getEpanetProfiles,
        saveProfile: this.saveEpanetProfile,
        editProfile: this.editEpanetProfile,
        deleteProfile: this.deleteEpanetProfile,
      };
    },
    watermetersDataSource() {
      return this.watermetersLayers?.find(layer => layer.id === this.watermetersParams.watermeters_layer)
        ?.data_source_name;
    },
    watermetersDataSourceAttrs() {
      return this.dataSources[this.watermetersDataSource]?.attributes_schema.attributes || [];
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers || [];
    },
    watermetersLayers() {
      return this.projectLayers?.filter(layer => layer.role === 'water_watermeters');
    },
    waterBalanceLayers() {
      return this.projectLayers?.filter(layer => ['linestring', 'multilinestring'].includes(layer.geometry_type));
    },
    waterBalanceLayerAttributes() {
      const currentLayer = this.layers[this.dmaJunctionsDemandParams.dma_layer];
      if (currentLayer) {
        const dataSourceAttributes = this.dataSources[currentLayer?.data_source_name]?.attributes_schema.attributes;
        const schemaForm = this.getForm(currentLayer?.form_schema.elements, dataSourceAttributes);
        return this.$_flattenStructure(schemaForm, 'elements');
      } else {
        return [];
      }
    },
    watermetersLayerAttrs() {
      return this.layers[this.watermetersParams.watermeters_layer]?.form_schema.elements || [];
    },
    watermetersAttrs() {
      const schemaForm = this.getForm(this.watermetersLayerAttrs, this.watermetersDataSourceAttrs);
      return this.$_flattenStructure(schemaForm, 'elements') || [];
    },
    watermetersInputs() {
      const inputs = [
        {
          name: 'watermeters_layer',
          items: this.watermetersLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          translationPath: 'sidebar.watermetersLayer',
          disabled: !this.useBillingData,
        },
        {
          name: 'watermeters_daily_usage',
          items: this.watermetersAttrs?.filter(
            attr =>
              ['integer', 'float', 'decimal'].includes(attr.dataType.name) &&
              attr.attribute !== this.watermetersParams.watermeters_recipient_kind
          ),
          itemText: 'label',
          itemValue: 'attribute',
          translationPath: 'sidebar.watermetersDailyUsageAttribute',
          disabled: !this.useBillingData || !this.watermetersParams.watermeters_layer,
        },
        {
          name: 'watermeters_recipient_kind',
          items: this.watermetersAttrs?.filter(
            attr => attr.attribute !== this.watermetersParams.watermeters_daily_usage
          ),
          itemText: 'label',
          itemValue: 'attribute',
          translationPath: 'sidebar.watermetersRecipientKindAttribute',
          disabled: !this.useBillingData || !this.watermetersParams.watermeters_layer,
        },
      ];
      return inputs.map(input => {
        return {
          ...input,
          dataType: { name: 'select' },
          rules: [v => !!v || v == '0' || 'rules.required'],
          hideRulesIcon: true,
          clearable: true,
          alignClass: 'start',
          paddingRight: 'pr-0',
        };
      });
    },
    dmaJunctionsDemandInputs() {
      const inputs = [
        {
          name: 'dma_layer',
          items: this.waterBalanceLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          translationPath: 'sidebar.waterBalanceOutputLayer',
          disabled: !this.use_dma_junctions_demand,
          events: {
            change: this.handleDmaLayerChange,
          },
        },
        {
          name: 'dma_demand',
          items: this.waterBalanceLayerAttributes.filter(attr =>
            ['integer', 'float', 'decimal'].includes(attr.dataType.name)
          ),
          itemText: 'label',
          itemValue: 'attribute',
          translationPath: 'sidebar.dmaJunctionsDemandCalculatedAttribute',
          disabled: !this.use_dma_junctions_demand || !this.dmaJunctionsDemandParams.dma_layer,
        },
        {
          name: 'dma_pipes',
          items: this.waterBalanceLayerAttributes,
          itemText: 'label',
          itemValue: 'attribute',
          translationPath: 'sidebar.waterPipesIdAttribute',
          disabled: !this.use_dma_junctions_demand || !this.dmaJunctionsDemandParams.dma_layer,
        },
      ];
      return inputs.map(input => {
        return {
          ...input,
          dataType: { name: 'select' },
          rules: [v => !!v || v == '0' || 'rules.required'],
          hideRulesIcon: true,
          clearable: true,
          alignClass: 'start',
          paddingRight: 'pr-0',
        };
      });
    },
  },
  methods: {
    exportFile: call('epanet/getEpanet'),
    runModel: call('epanet/runModel'),
    getStatus: call('epanet/getStatus'),
    getNetKind: call('epanet/getNetKind'),
    getEpanetProfiles: call('epanet/getEpanetProfiles'),
    saveEpanetProfile: call('epanet/saveEpanetProfile'),
    editEpanetProfile: call('epanet/editEpanetProfile'),
    deleteEpanetProfile: call('epanet/deleteEpanetProfile'),
    getSpecificDictionary: call('admin/getSpecificDictionary'),

    async init() {
      await Promise.all([this.getStatus({}), this.getNetKind({})]);
      if (this.isNetDevelopmentPlaningEnabled)
        this.netDevelopmentDictionary = await this.getSpecificDictionary(this.developmentPlanningDictId);
      this.isStickyButtonsVisible = true;
      this.isStickyButtonsValid = this.isValid;
      this.stickyButtons = this.computedStickybuttons;
      this.$root.$off('generateGraph');
      this.$root.$on('generateGraph', this.generateGraph);
      this.$root.$off('epanetAnalysisModel');
      this.$root.$on('epanetAnalysisModel', this.epanetAnalysisModel);
      this.isFetched = true;
    },
    setLocalStorageItems() {
      localStorage.setItem('statusModel', JSON.stringify(this.statusModel));
      localStorage.setItem('netKindModel', JSON.stringify(this.netKindModel));
      localStorage.setItem('useBillingData', JSON.stringify(this.useBillingData));
      localStorage.setItem('useScadaData', JSON.stringify(this.useScadaData));
      localStorage.setItem('scada_since', JSON.stringify(this.scada_since));
      localStorage.setItem('billing_demand_method', JSON.stringify(this.billing_demand_method));
      localStorage.setItem('billing_use_base_demand', JSON.stringify(this.billing_use_base_demand));
      localStorage.setItem('additionalGraphParameters', JSON.stringify(this.additionalGraphParameters));
      localStorage.setItem('use_dma_junctions_demand', JSON.stringify(this.use_dma_junctions_demand));
      localStorage.setItem('dma_layer', JSON.stringify(this.dmaJunctionsDemandParams.dma_layer));
      localStorage.setItem('dma_demand', JSON.stringify(this.dmaJunctionsDemandParams.dma_demand));
      localStorage.setItem('dma_pipes', JSON.stringify(this.dmaJunctionsDemandParams.dma_pipes));
      localStorage.setItem('netDevelopmentVariant', JSON.stringify(this.netDevelopmentVariant));
      localStorage.setItem('useNetDevelopmentPlaning', JSON.stringify(this.useNetDevelopmentPlaning));
    },
    getPayload() {
      let additionalParamsArray = [];
      this.additionalGraphParameters.forEach(param => {
        if (param.checked) {
          additionalParamsArray.push({
            filter: param.filter,
            datasource: param.datasource,
          });
        }
      });
      if (this.isNetDevelopmentPlaningEnabled && this.useNetDevelopmentPlaning && this.netDevelopmentVariant) {
        additionalParamsArray.push({
          filter: {
            '!OR': [
              {
                $EQUAL: {
                  [`water_pipes.${this.developmentPlanningAttribute}`]: {
                    value: this.netDevelopmentVariant,
                  },
                },
              },
              {
                IS_NULL: `water_pipes.${this.developmentPlanningAttribute}`,
              },
            ],
          },
          datasource: 'water_pipes',
        });
      }
      const payload = {
        status: this.statusModel,
        net_kind: this.netKindModel,
        billing: this.useBillingData,
        scada: this.useScadaData,
        billing_use_base_demand: this.billing_use_base_demand,
        ...(this.useScadaData && { scada_since: this.scada_since }),
        ...(this.useBillingData &&
          this.computedWaterUsage.length === 2 && { billing_since: this.computedWaterUsage[0] }),
        ...(this.useBillingData &&
          this.computedWaterUsage.length === 2 && { billing_until: this.computedWaterUsage[1] }),
        ...(this.useBillingData && { billing_demand_method: this.billing_demand_method }),
        features_filter: additionalParamsArray,
        ...(this.useBillingData && this.isEpanetDailyUsageEnabled && { ...this.watermetersParams }),
        ...(this.isUseDmaJunctionsDemandEnabled &&
          this.use_dma_junctions_demand && { dma_junctions_demand: { ...this.dmaJunctionsDemandParams } }),
      };
      return payload;
    },
    getProfilePayload() {
      let payload = {
        ...this.getPayload(),
        ...(this.getPayload().dma_junctions_demand && {
          dma_layer: this.dmaJunctionsDemandParams.dma_layer,
          dma_pipes: this.dmaJunctionsDemandParams.dma_pipes,
          dma_demand: this.dmaJunctionsDemandParams.dma_demand,
        }),
        features_filter: this.additionalGraphParameters,
      };
      delete payload.scada;
      delete payload.scada_since;
      delete payload.dma_junctions_demand;
      delete payload.use_dma_junctions_demand;
      return payload;
    },
    changeCurrentEpanetProfile(newProfile) {
      if (newProfile) {
        this.statusModel = newProfile.status;
        this.netKindModel = newProfile.net_kind;
        this.useBillingData = newProfile.billing;
        this.billing_demand_method = newProfile.billing_demand_method;
        this.additionalGraphParameters = newProfile.features_filter;
        this.billing_use_base_demand = newProfile.billing_use_base_demand;
        if (newProfile.watermeters_layer && this.projectLayers.find(x => x.layer_id === newProfile.watermeters_layer)) {
          this.watermetersParams.watermeters_daily_usage = newProfile.watermeters_daily_usage;
          this.watermetersParams.watermeters_layer = newProfile.watermeters_layer;
          this.watermetersParams.watermeters_recipient_kind = newProfile.watermeters_recipient_kind;
        } else {
          this.$nextTick(() => {
            this.$refs.watermetersForm?.$refs.form?.reset();
          });
        }
        this.use_dma_junctions_demand = newProfile.dma_layer ? true : false;
        if (newProfile.dma_layer && this.projectLayers.find(x => x.layer_id === newProfile.dma_layer)) {
          this.dmaJunctionsDemandParams.dma_layer = newProfile.dma_layer;
          this.dmaJunctionsDemandParams.dma_pipes = newProfile.dma_pipes;
          this.dmaJunctionsDemandParams.dma_demand = newProfile.dma_demand;
        } else {
          this.$nextTick(() => {
            this.$refs.dmaJunctionsDemandForm?.reset();
          });
        }
        const usage =
          newProfile.billing_since && newProfile.billing_until
            ? [
                this.$dayjs(newProfile.billing_since).format('YYYY-MM'),
                this.$dayjs(newProfile.billing_until).format('YYYY-MM'),
              ]
            : [];
        this.waterUsageDate = usage;
      }
    },
    handleDmaLayerChange() {
      const layer = this.dmaJunctionsDemandParams.dma_layer;
      this.$refs.dmaJunctionsDemandForm?.reset();
      this.dmaJunctionsDemandParams.dma_layer = layer;
    },
    async epanetAnalysisModel() {
      this.isModelLoading = true;
      try {
        const r = await this.runModel(this.getPayload());
        this.$router.push({
          name: 'modelingResultsScenario',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isModelLoading = false;
      }
    },
    async generateGraph() {
      this.isExportLoading = true;
      this.setLocalStorageItems();
      try {
        return await this.exportFile(this.getPayload());
      } finally {
        this.isExportLoading = false;
      }
    },
  },
  watch: {
    isValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    computedStickybuttons(nV) {
      this.stickyButtons = nV;
    },
    isScadaJunctionsEnabled: {
      immediate: true,
      handler(nV) {
        if (!nV) this.useScadaData = nV;
      },
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
  },
};
</script>

<style scoped lang="scss">
.billingDataText {
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  color: #202124;
}
.billingDataTextDisabled {
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.38);
}
::v-deep {
  .v-input--selection-controls {
    margin-top: 0;
  }
  .attrs_form--disabled .sidebar-form-name {
    color: rgba(0, 0, 0, 0.38) !important;
  }
}
</style>
